import { type ReactNode, useMemo } from 'react'
import { useLocales } from '@sevenrooms/locales'
import { useTheme } from '@sevenrooms/ui-kit'
import { IconButton } from '@sevenrooms/ui-kit/form'
import { useMaxWidthBreakpoint, useScrollController } from '@sevenrooms/ui-kit/hooks'
import { VStack, HStack, Flex, Box } from '@sevenrooms/ui-kit/layout'
import { Text } from '@sevenrooms/ui-kit/typography'
import { getTimeslotDescription } from '../../../components/PrimaryAvailability/ReservationAvailability'
import { ReservationTimeslot, type ReservationTimeSlotVariant } from '../../../components/ReservationTimeslot'
import { TimeSlotStack } from '../../../components/TimeSlotStack'
import type { AvailabilityTimeWithUpSellCost } from '../../../hooks'

const timeslotShortVariantWidth = '113px'

interface RowTimeSlotsProps {
  timeslots?: AvailabilityTimeWithUpSellCost[]
  timeSlotVariant?: ReservationTimeSlotVariant
  onTimeSlotClick?: (slot: AvailabilityTimeWithUpSellCost) => void
  requestOrPriorityAlertAvailability?: ReactNode
  startDate?: string
  descriptionType?: 'short' | 'full'
  currencyCode: string
}

export function RowTimeSlots({
  timeslots,
  timeSlotVariant = 'primary',
  descriptionType = 'full',
  onTimeSlotClick,
  startDate,
  requestOrPriorityAlertAvailability,
  currencyCode,
}: RowTimeSlotsProps) {
  const { formatMessage } = useLocales()
  const { spacing } = useTheme()
  const isMobile = useMaxWidthBreakpoint('s')
  const timeslotWidth = useMemo(() => {
    const buttonsCount = timeslots?.length || 0 + (requestOrPriorityAlertAvailability ? 1 : 0)
    if (buttonsCount < 4) {
      return '100%'
    }
    return timeslotShortVariantWidth
  }, [requestOrPriorityAlertAvailability, timeslots?.length])
  const scrollStep = (parseInt(timeslotWidth) + parseInt(spacing.sm)) * 2 + 1
  const { ref, scrollLeft, scrollRight, canScrollLeft, canScrollRight } = useScrollController(scrollStep, 'smooth')
  return (
    <VStack>
      <HStack justifyContent={startDate ? 'space-between' : 'right'} alignItems="center" height="32px">
        {startDate && (
          <Flex>
            <Text data-test="sr-selected-date">{startDate}</Text>
          </Flex>
        )}
        {!isMobile && (canScrollLeft || canScrollRight) && (
          <Flex flex="0 0 auto" ml="s">
            <IconButton
              borderType="none-round"
              size="s"
              icon="VMSWeb-chevron-left"
              data-test="sr-room-card-left-button"
              aria-label="scroll left"
              onClick={scrollLeft}
              disabled={!canScrollLeft}
            />
            <IconButton
              borderType="none-round"
              size="s"
              icon="VMSWeb-chevron-right"
              data-test="sr-room-card-right-button"
              aria-label="scroll right"
              onClick={scrollRight}
              disabled={!canScrollRight}
            />
          </Flex>
        )}
      </HStack>
      <TimeSlotStack
        data-test="sr-room-card-availability-container"
        isMobile={isMobile}
        width="100%"
        spacing="sm"
        justifyContent="flex-start"
        flexWrap="nowrap"
        ref={ref}
      >
        {timeslots?.map(timeSlotInfo => (
          <ReservationTimeslot
            variant={timeSlotVariant}
            size="l"
            width={timeslotWidth}
            key={`${timeSlotInfo.time}${timeSlotInfo.accessPersistentId ?? ''}`}
            timeslotDescription={
              descriptionType === 'full'
                ? getTimeslotDescription(
                    formatMessage,
                    timeSlotInfo.publicTimeSlotDescription,
                    timeSlotInfo.showCost,
                    (timeSlotInfo.cost ?? 0) + timeSlotInfo.includedUpgradesCost,
                    timeSlotInfo.chargeType,
                    timeSlotInfo.duration,
                    currencyCode,
                    timeSlotInfo.fees
                  )
                : timeSlotInfo.publicTimeSlotDescription
            }
            time={timeSlotInfo.time}
            onClick={() => {
              onTimeSlotClick?.(timeSlotInfo)
            }}
          />
        ))}
        <Box minWidth={timeslotWidth}>{requestOrPriorityAlertAvailability}</Box>
      </TimeSlotStack>
    </VStack>
  )
}
